export type AdminStatusTone =
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "neutral"
  | "emerald"
  | "blue"
  | "amber"
  | "orange"
  | "red"
  | "rose"
  | "slate"
  | "violet"
  | "teal";

export type AdminStatusDisplay = {
  label: string;
  tone: AdminStatusTone;
};

export const adminStatusToneClass: Record<AdminStatusTone, { bg: string; text: string; border: string; dot: string }> = {
  success: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" },
  warning: { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200", dot: "bg-orange-500" },
  danger: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", dot: "bg-red-500" },
  info: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", dot: "bg-blue-500" },
  neutral: { bg: "bg-slate-50", text: "text-slate-500", border: "border-slate-200", dot: "bg-slate-400" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" },
  blue: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", dot: "bg-blue-500" },
  amber: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500" },
  orange: { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200", dot: "bg-orange-500" },
  red: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", dot: "bg-red-500" },
  rose: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", dot: "bg-rose-500" },
  slate: { bg: "bg-slate-50", text: "text-slate-500", border: "border-slate-200", dot: "bg-slate-400" },
  violet: { bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200", dot: "bg-violet-500" },
  teal: { bg: "bg-teal-50", text: "text-teal-700", border: "border-teal-200", dot: "bg-teal-500" },
};

const sessionStatusFallback: Record<string, AdminStatusDisplay> = {
  SUCCESS: { label: "성공", tone: "emerald" },
  COMPLETED: { label: "완료", tone: "emerald" },
  NO_RESPONSE: { label: "무응답", tone: "orange" },
  FAILED: { label: "실패", tone: "red" },
  IN_PROGRESS: { label: "진행 중", tone: "blue" },
};

const conversationResultTypeFallback: Record<string, AdminStatusDisplay> = {
  OK: { label: "응답 완료", tone: "emerald" },
  NO_RESPONSE: { label: "무응답", tone: "orange" },
  FAILED: { label: "확인 실패", tone: "red" },
  UNKNOWN: { label: "확인 중", tone: "slate" },
};

const yesNoFallback: Record<string, AdminStatusDisplay> = {
  Y: { label: "성공", tone: "emerald" },
  N: { label: "실패", tone: "red" },
};

const memberStatusFallback: Record<string, AdminStatusDisplay> = {
  ACTIVE: { label: "활성", tone: "emerald" },
  INACTIVE: { label: "비활성", tone: "slate" },
  DELETED: { label: "삭제", tone: "rose" },
};

const memberRoleFallback: Record<string, AdminStatusDisplay> = {
  ADMIN: { label: "관리자", tone: "blue" },
  ELDERLY: { label: "어르신", tone: "teal" },
  FAMILY: { label: "보호자", tone: "violet" },
};

const emergencyEventStatusFallback: Record<string, AdminStatusDisplay> = {
  OPEN: { label: "미처리", tone: "red" },
  ACKNOWLEDGED: { label: "확인", tone: "orange" },
  IN_PROGRESS: { label: "처리 중", tone: "orange" },
  RESOLVED: { label: "처리 완료", tone: "emerald" },
};

const emergencyRiskLevelFallback: Record<string, AdminStatusDisplay> = {
  HIGH: { label: "높음", tone: "red" },
  MEDIUM: { label: "보통", tone: "orange" },
  LOW: { label: "낮음", tone: "emerald" },
};

const emergencyEventTypeFallback: Record<string, AdminStatusDisplay> = {
  SOS: { label: "긴급 호출", tone: "red" },
  NO_RESPONSE: { label: "무응답", tone: "orange" },
  NO_RESPONSE_FINAL: { label: "최종 무응답", tone: "orange" },
  RISK_SOUND: { label: "주의 소리", tone: "amber" },
  INACTIVE_LONG: { label: "장시간 미활동", tone: "orange" },
  EMERGENCY: { label: "긴급", tone: "red" },
};

const notificationSendStatusFallback: Record<string, AdminStatusDisplay> = {
  SENT: { label: "발송 완료", tone: "emerald" },
  SUCCESS: { label: "성공", tone: "emerald" },
  COMPLETED: { label: "완료", tone: "emerald" },
  PENDING: { label: "대기", tone: "amber" },
  FAILED: { label: "실패", tone: "red" },
};

const notificationTypeFallback: Record<string, AdminStatusDisplay> = {
  PUSH: { label: "푸시 알림", tone: "blue" },
  SMS: { label: "문자", tone: "blue" },
  KAKAO: { label: "카카오 알림", tone: "amber" },
  CALL: { label: "전화", tone: "emerald" },
};

const fallbackGroups: Record<string, Record<string, AdminStatusDisplay>> = {
  CARE_SESSION_STATUS: sessionStatusFallback,
  CONVERSATION_RESULT_TYPE: conversationResultTypeFallback,
  YES_NO: yesNoFallback,
  MEMBER_STATUS: memberStatusFallback,
  MEMBER_ROLE: memberRoleFallback,
  EMERGENCY_EVENT_STATUS: emergencyEventStatusFallback,
  EMERGENCY_RISK_LEVEL: emergencyRiskLevelFallback,
  EMERGENCY_EVENT_TYPE: emergencyEventTypeFallback,
  NOTIFICATION_SEND_STATUS: notificationSendStatusFallback,
  NOTIFICATION_TYPE: notificationTypeFallback,
};

const severityToneMap: Record<string, AdminStatusTone> = {
  NORMAL: "emerald",
  INFO: "blue",
  WARNING: "orange",
  ATTENTION: "orange",
  DANGER: "red",
  CRITICAL: "red",
  ERROR: "red",
};

export const resolveAdminStatusDisplay = (params: {
  group: string;
  code: string | null | undefined;
  name?: string | null;
  shortName?: string | null;
  severity?: string | null;
  fallbackTone?: AdminStatusTone;
}): AdminStatusDisplay => {
  const code = params.code ?? "";
  const fallback = fallbackGroups[params.group]?.[code];
  const label = params.shortName || params.name || fallback?.label || code || "-";
  const tone = params.severity ? severityToneMap[params.severity] ?? fallback?.tone : fallback?.tone;

  return {
    label,
    tone: tone ?? params.fallbackTone ?? "slate",
  };
};
